import isPlainObject from 'lodash/isPlainObject';
import hoistNonReactStatics from 'hoist-non-react-statics';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import React from 'react';
import { customAlphabet } from 'nanoid';
import { componentMap as featureComponentMap } from '../manager/feature';
import DataSourceSetter from '../setter/datasource-setter/datasource-setter';
import { getFormattedValue } from '../comoponents/util';
import {
  __dataSource__,
  __extraParam__,
  __record__,
  __tableCurrentRow__,
  dataOriginStatic,
} from './const';
import { __statisticCard_activeKey__ } from './expr-const';

const { VisualEngine } = window;
const Engine = VisualEngine;

export function executeFunction(func, ...rest) {
  if (typeof func === 'function') {
    let result;
    try {
      result = func(...rest);
    } catch (e) {}
    return result;
  }
}

function isDesignMode(props) {
  let mode;
  if (isPlainObject(props)) {
    mode = props?.__designMode;
  } else if (typeof props === 'string') {
    mode = props;
  }
  return mode === 'design';
}

function isArrayNotEmpty(list) {
  return Array.isArray(list) && list.length > 0;
}

function isArrayEmpty(list) {
  return Array.isArray(list) && list.length === 0;
}

function judgeDataEmpty(options) {
  if (options) {
    if (isArrayEmpty(options?.data)) {
      return true;
    }
    if (isArrayEmpty(options.children?.[0]?.data)) {
      return true;
    }
    if (isArrayEmpty(options?.dataset.source)) {
      return true;
    }
  }
  return false;
}

function getRealResponse(res: { status: number; data: string } | any) {
  const { status, data } = res;
  let result = res;
  if (status === 200 && data) {
    result = data;
  }
  return result;
}

export function isValueObjectExpr(obj) {
  return !!(isPlainObject(obj) && obj.group && obj.param);
}

export function calculateTextExprValue(originValue, config = {}) {
  const {
    urlParamsDataSource,
    recordDataSource,
    state,
    extraParamList = [],
    tableCurrentRow,
    extraParam,
    getExtraParam,
  } = config;
  if (typeof originValue === 'function') {
    try {
      let extra = extraParamList;
      if (!Array.isArray(extra)) {
        extra = [];
      }
      return originValue(recordDataSource, state, ...extra);
    } catch (e) {
      return;
    }
  } else if (isValueObjectExpr(originValue)) {
    const { group, param, secondParam, thirdParam } = originValue;
    if (group && param) {
      let result;
      let realExtraParam = extraParam;
      if (typeof getExtraParam === 'function') {
        realExtraParam = executeFunction(getExtraParam);
      }
      const objExprArgMap = {
        [__record__]: recordDataSource,
        [__dataSource__]: state,
        [__tableCurrentRow__]: tableCurrentRow,
        [__extraParam__]: realExtraParam,
      };
      if (secondParam) {
        result = get(
          objExprArgMap,
          `${group}.${param}.${secondParam}${thirdParam ? `.${thirdParam}` : ''}`,
        );
      } else {
        result = get(objExprArgMap, `${group}.${param}`);
      }
      return result;
    }
  }
  return originValue;
}

function handleI18nLabel(label) {
  if (isPlainObject(label) && label.type === 'cn-i18n') {
    return label.dm || '';
  }
  return label;
}

function getChartCommonStyle(props) {
  const { chartStyle } = props || {};
  return {
    containerStyle: {
      height: chartStyle?.height,
    },
  };
}

function getNodeById(config) {
  const { id } = config;
  let result;
  if (id) {
    const nodesMap = window?.AliLowCodeEngine?.designer?.currentDocument?.nodesMap;
    if (nodesMap?.values()) {
      for (const item of nodesMap.values()) {
        if (item?.id === id) {
          result = item;
          break;
        }
      }
    }
  }
  return result;
}

function makeDesignContext() {
  const state: Record<string, any> = {};
  const nodesMap = window?.AliLowCodeEngine?.designer?.currentDocument?.nodesMap;
  const values = nodesMap?.values();
  if (values) {
    for (const item of values) {
      const propsData = item?.propsData || {};
      const { _dataSourceName, defaultParams, config } = propsData || {};
      const { dataOrigin, defaultValue } = defaultParams || {};
      if (item.componentName === 'CnStatisticCardList') {
        const defaultValue = getFormattedValue(item?.propsData?.selectOpts?.defaultActiveKey);
        state[_dataSourceName] = {
          [__statisticCard_activeKey__]: defaultValue,
        };
      }
      if (_dataSourceName && dataOrigin === dataOriginStatic) {
        if (isPlainObject(defaultValue)) {
          state[_dataSourceName] = defaultValue;
        } else {
          const temp = window?.cnLowCodeUtils?.util?.getFormDefaultValue?.(defaultParams, {
            formConfig: config,
            ignoreDefaultSelectFormRequest: true,
          });
          if (isPlainObject(temp)) {
            state[_dataSourceName] = temp;
          }
        }
      }
    }
  }

  return {
    state,
    $: (selector) => {
      return {
        props: getNodeById({ id: selector })?.propsData || {},
      };
    },
  };
}

function getSlotDom(config) {
  const { componentSlot, isDesign, componentProps } = config || {};
  if (componentSlot) {
    if (isArrayNotEmpty(componentSlot)) {
      return componentSlot.map((item, index) => {
        return React.cloneElement(item, componentProps || {});
      });
    }
    return componentSlot;
  }
  return null;
}

export function getItem(componentMap, position, componentName, propKey, extraQueryParams) {
  let result;
  if (componentName) {
    let item = componentMap[componentName];
    if (typeof item === 'function') {
      item = executeFunction(item, { position });
    }
    let flag = true;
    if (item && item.position && item.position.includes(position)) {
    } else {
      flag = false;
    }
    if (isPlainObject(extraQueryParams)) {
      for (const key in extraQueryParams) {
        if (key && Array.isArray(item?.[key])) {
          if (item[key].includes(extraQueryParams[key])) {
          } else {
            flag = false;
          }
        }
      }
    }
    if (flag) {
      if (!item.label) {
        item.label = item.title;
      }
      if (!item.value) {
        item.value = item.componentName;
      }
      if (propKey) {
        result = item[propKey];
      } else {
        result = item;
      }
    }
  }
  return result;
}

export function condition(prop, type, primaryKey) {
  return prop?.parent?.parent?.getPropValue?.(primaryKey) === type;
}

export function handlePrototypeCondition(prototypeList = [], componentName, primaryKey) {
  if (prototypeList?.length > 0) {
    return prototypeList.map((item) => {
      let newItem = item;
      if (isPlainObject(item)) {
        newItem = { ...item };
        newItem.__optType = componentName;
      }
      if (!newItem?.condition) {
        newItem.condition = function (prop) {
          return condition(prop, componentName, primaryKey);
        };
      }
      return newItem;
    });
  }
  return prototypeList;
}

function getItemPrototypeListByPosition(config) {
  const { position, primaryKey, componentMap } = config;
  const defaultList = Object.keys(componentMap);
  const bizComponentNameList = getBizComponentNameList();
  let prototypeList: any = [];
  const allComponentList = uniq([...defaultList, ...bizComponentNameList]);
  allComponentList.forEach((name) => {
    const item = getItem(componentMap, position, name) || {};
    const { getPrototypeList, configure = [] } = item;
    if (typeof getPrototypeList === 'function') {
      const temp = getPrototypeList(position);
      if (temp && temp.length > 0) {
        prototypeList = [...prototypeList, ...handlePrototypeCondition(temp, name, primaryKey)];
      }
    } else if (configure?.length > 0) {
      prototypeList = [...prototypeList, ...handlePrototypeCondition(configure, name, primaryKey)];
    }
  });
  return prototypeList;
}

export function getItemListByPosition(config) {
  const { position, componentMap, extraQueryParams } = config;
  const result = [];
  const defaultList = Object.keys(componentMap);
  const bizComponentNameList = getBizComponentNameList();
  const allComponentList = uniq([...defaultList, ...bizComponentNameList]);
  allComponentList.forEach((name) => {
    const component = getItem(componentMap, position, name, null, extraQueryParams);
    if (component) {
      const { bizInfo = [] } = component;
      if (bizInfo.length > 0) {
        bizInfo.forEach((item) => {
          const { label, value } = item;
          const existGroup = result.find((item2) => item2.value === value);
          if (existGroup) {
            existGroup?.children.push(component);
          } else {
            result.push({
              title: label,
              value,
              children: [component],
            });
          }
        });
        return;
      }
      result.push(component);
    }
  });
  return result;
}

// 获取业务组件列表
function getBizComponentNameList() {
  let result = [];
  if (VisualEngine) {
    const componentsMap = VisualEngine?.designer?.getComponentsMap?.();
    if (componentsMap) {
      result = Object.keys(componentsMap);
    }
  }
  return result;
}

export function getItemDefaultProps(componentMap, position, componentName) {
  const func = getItem(componentMap, position, componentName, 'getDefaultProps');
  if (typeof func === 'function') {
    return func();
  }
}

function calculateFeature(config) {
  const { options, props, position, ...rest } = config || {};
  const { __featureConfig__ } = props || {};
  let result = options;
  if (isArrayNotEmpty(__featureConfig__)) {
    for (const item of __featureConfig__) {
      const { name, active } = item || {};
      if (name && active) {
        const handle = getItem(featureComponentMap, position, name, 'handleFeature');
        const tempResult = handle?.({
          featureItemConfig: item,
          props,
          options,
          position,
          ...rest,
        });
        if (isPlainObject(tempResult)) {
          result = tempResult;
        }
      }
    }
  }
  return result;
}

function getJSExpressionPrototype(config) {
  const { type: configType, prototypeConfig } = config || {};
  if (configType === 'base') {
    return getJSExpressionPrototypeTemplate({
      prototypeConfig,
      initialCode: `(arg, state) => {
  // return state.urlParams.xxx;
}`,
      tip: `
state：全部的数据，在左侧列表中选择使用。
`,
    });
  }
  return {};
}

export function getJSExpressionPrototypeTemplate(config) {
  const { title, initialCode, tip, prototypeConfig } = config || {};
  return {
    title: title || '写js表达式',
    componentName: 'CnRowDataSetter',
    props: () => {
      const stateList = getDataSourceListWithAllPath({
        needChildren: true,
        prototypeConfig,
      });
      return {
        list: stateList,
        initialCode,
        tip,
      };
    },
  };
}

function getDataSourceListWithAllPath(config) {
  const { typeList } = config || {};
  const dataSource = VisualEngine?.context?.getManager('dataPool');
  const items = dataSource?.getItems();
  const result = [];
  if (items?.length > 0) {
    items.forEach((item) => {
      const { name, type, config: dsConfig } = item;
      const label = dsConfig?.description || name;
      const ds = {
        text: label,
        label,
        value: `state.${name}`,
      };
      if (Array.isArray(typeList) && typeList.length > 0) {
        if (typeList.includes(type)) {
          result.push(ds);
        }
      } else {
        result.push(ds);
      }
    });
  }
  return result;
}

function calculateAdvancedConfig(config) {
  const { options, props, state } = config || {};
  const { __advancedConfig__ } = props || {};
  const { handleProps } = __advancedConfig__ || {};
  if (typeof handleProps === 'function') {
    const result = executeFunction(handleProps, options, state);
    if (isPlainObject(result)) {
      return result;
    }
  }
  return options;
}

const getDsNameFromVariable = (dsExpr) => {
  let result = '';
  if (dsExpr && dsExpr.variable) {
    result = dsExpr.variable.slice(dsExpr.variable.lastIndexOf('.') + 1);
  }
  return result;
};

const getDataSourceList = (config) => {
  const { typeList, needChildren } = config || {};
  const dataSource = VisualEngine?.context?.getManager('dataPool');
  const items = dataSource?.getItems();
  const result = [];
  if (items?.length > 0) {
    items.forEach((item) => {
      const { id, name, type, config: dsConfig } = item;
      const label = dsConfig?.description || name;
      const ds = {
        text: label,
        label,
        value: name,
        dataSourceId: dsConfig?.id,
        id,
      };
      if (Array.isArray(typeList) && typeList.length > 0) {
        if (typeList.includes(type)) {
          result.push(ds);
        }
      } else {
        result.push(ds);
      }
    });
  }
  return result;
};

const generateIndex = (dataSourceList, componentName, key = 'value') => {
  let index = 2;
  while (dataSourceList.find((item) => item[key] === `${componentName}${index}`)) {
    index++;
  }
  return index;
};

function generateDataSource(config) {
  const { componentName } = config || {};
  if (componentName) {
  }
  let dataSourceList = [];
  dataSourceList = getDataSourceList();
  let index = '';
  const first = dataSourceList?.find((item) => item.value === `${componentName}`);
  if (first) {
    index = generateIndex(dataSourceList, componentName);
  }
  if (Engine) {
    const componentPrototype = Engine?.designer?.getComponentMeta?.(componentName)?.prototype;
    const dataSource = Engine?.context?.getManager('dataPool');
    const ds = dataSource?.addItem({
      description: `${componentPrototype?.options?.title || componentName}${index}的数据源`,
      dpType: 'VALUE',
      id: '',
      initialData: '',
      isGlobal: false,
      name: `${componentName}${index}`,
      protocal: 'VALUE',
    });
    if (ds?.name) {
      return ds;
    }
  }
}

function createDataSourceSetters() {
  return [
    {
      name: '_dataSource',
      title: '数据源',
      display: 'inline',
      tab: 'advanced',
      supportVariable: false,
      mutator(value) {
        const node = this.getProps().getNode();
        const result = getDsNameFromVariable(value);
        if (result) {
          node.setPropValue('_dataSourceName', result);
        }
      },
      initialValue(value) {
        const v = this.getValue();
        // if (value) {
        //   return value;
        // }
        // if(Engine?.Exchange?.hoveringNode){
        //   const node = this.getNode();
        //   const {componentName} = node;
        //   const currentDataSource = node.getPropValue('_dataSource');
        const { componentName } = this;
        const currentDataSource = this.getPropValue('_dataSource');
        if (!currentDataSource && !v) {
          const ds = generateDataSource({ componentName });
          if (ds?.name) {
            // this.setPropValue('_dataSourceName',ds.name)
            this.parent?.setPropValue?.('_dataSourceName', ds.name);
            return {
              type: 'variable',
              variable: `state.${ds.name}`,
            };
          }
        }
      },
      setter: <DataSourceSetter />,
    },
    {
      name: '_dataSourceName',
      title: '数据源名称',
      display: 'none',
      supportVariable: true,
      initialValue() {
        const value = this.parent?.getPropValue?.('_dataSourceName');
        if (!value) {
          const node = this.getNode();
          const dsExpr = node?.getPropValue('_dataSource');
          return getDsNameFromVariable(dsExpr);
        }
        return value;
      },
    },
  ];
}

function getLowCodeCoreUtil(path: string) {
  return get(window.cnLowCodeUtils, path);
}

function setDataToDs(config: any) {
  const { data, _dataSourceName, _context, _dataSource } = config || {};
  if (isPlainObject(data) && _dataSourceName && _context) {
    _context?.setState?.({
      [_dataSourceName]: {
        ..._dataSource,
        ...data,
      },
    });
  }
}

function uuid(len = 4) {
  return customAlphabet('abcdefghijklmnopqrstuvwxyz', len)();
}

// 自动装配rerender 方法
function AutoWireRerender(SourceComp: React.ComponentClass) {
  function useUpdate() {
    const [, setState] = React.useState({});
    return React.useCallback(() => {
      setState({});
    }, []);
  }
  const UpdateComp = React.forwardRef((props, ref) => {
    const update = useUpdate();
    React.useImperativeHandle(ref, () => ({
      reRender() {
        update();
      },
    }));
    return <SourceComp {...props} />;
  });
  class Wrapper extends React.Component {
    ref = React.createRef();
    constructor(props) {
      super(props);
    }

    reRender = () => {
      this.ref?.current?.reRender?.();
    };

    render() {
      return <UpdateComp ref={this.ref} {...this.props} />;
    }
  }

  hoistNonReactStatics(Wrapper, SourceComp);
  Wrapper.displayName = SourceComp.displayName;
  return Wrapper;
}

// 装配rerender效果配置名单
const rerenderOpenList = ['CnPieChart', 'CnLineChart', 'CnProgress', 'CnColumnChart'];
function AutoWireViewWithRerender(
  compList: React.FunctionComponent[] | React.ClassicComponentClass[],
) {
  return compList?.map((compItem) =>
    rerenderOpenList.includes(compItem?.displayName) ? AutoWireRerender(compItem) : compItem,
  );
}

function handleFormatChildren(children, config) {
  const { item, index, state, _context } = config || {};
  if (children && item) {
    return React.Children.map(children, (item2) => {
      if (item2?.props?.__componentName__ === 'CnFormat' || item2?.props?.isCnFormat === true) {
        return React.cloneElement(item2, {
          __getContextData__: () => {
            return {
              functionArg: {
                item,
                index,
                state,
                _context,
              },
            };
          },
        });
      }
      return item2;
    });
  } else {
    return children;
  }
}

function initArray(n: number) {
  const arr = [];
  for (let i = 0; i < n; i++) {
    arr.push(i);
  }
  return arr;
}

export {
  isDesignMode,
  isArrayNotEmpty,
  getRealResponse,
  handleI18nLabel,
  getChartCommonStyle,
  getSlotDom,
  getItemPrototypeListByPosition,
  calculateFeature,
  getJSExpressionPrototype,
  calculateAdvancedConfig,
  createDataSourceSetters,
  getDataSourceList,
  isArrayEmpty,
  judgeDataEmpty,
  getLowCodeCoreUtil,
  setDataToDs,
  uuid,
  AutoWireViewWithRerender,
  makeDesignContext,
  handleFormatChildren,
  initArray,
};
